import {
  APIClient,
  APIClientSpec,
  Options as ClientResourceOptions,
  ClientResponse,
  ClientSpec,
} from '@b2chat/chat-center-sdk';
import { miniSerializeError, SerializedError } from '@reduxjs/toolkit';
import isPublicRoute from '@utils/isPublicRoute';
import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { BaseQueryFn, retry } from '@reduxjs/toolkit/dist/query/react';
import env from '@utils/env';
import _ from 'lodash';

APIClient.config.jwtBaseURL = env.WEB_SERVICE_BASE_URI;

const createClientSpec = (
  newSpect: Omit<ClientSpec, 'resources'>
): typeof APIClientSpec => _.merge({}, APIClientSpec, newSpect);

//BACKOFFICE
const spec = createClientSpec({
  baseURL: env.WEB_SERVICE_BASE_URI,
  logRequests: env.APP_ENV !== 'production',
  headers: {
    /**
     * Which build is talking, on every backoffice request.
     *
     * The service worker hands a new version over only when the user clicks the update toast, so a
     * console left open for days keeps running an old bundle. On 2026-09-18 that was invisible from
     * the server: telling a stale client from a current one meant phoning the user. Sent here and
     * nowhere else because botbackoffice is the only service whose CORS is verified `allowedHeaders("*")`
     * — adding it to the other seven specs would need each of their configs checked first.
     */
    'X-App-Version': env.VERSION || 'unknown',
  },
});
const B2ChatClient = new APIClient(spec);

//BROADCAST
const spec2 = createClientSpec({
  baseURL: env.WEB_SERVICE_BROADCAST_BASE_URI,
  logRequests: env.APP_ENV !== 'production',
});
export const B2ChatClientBroadcast = new APIClient(spec2);

//ECOMMERCE
const spec3 = createClientSpec({
  baseURL: env.WEB_SERVICE_ECOMMERCE_URI,
  logRequests: env.APP_ENV !== 'production',
});
export const B2ChatClientEcommerce = new APIClient(spec3);

//CONTACTS
const spec4 = createClientSpec({
  baseURL: env.WEB_SERVICE_CONTACTS_BASE_URI,
  logRequests: env.APP_ENV !== 'production',
});
export const B2ChatClientContacts = new APIClient(spec4);

//ASSISTANTS
const spec5 = createClientSpec({
  baseURL: env.WEB_SERVICE_ASSISTANTS_BASE_URI,
  logRequests: env.APP_ENV !== 'production',
});
export const B2ChatClientAssistants = new APIClient(spec5);

//CATALOG
const spec7 = createClientSpec({
  withCredentials: false,
  baseURL: env.WEB_SERVICE_CATALOG_BASE_URI,
  logRequests: env.APP_ENV !== 'production',
});

export const B2ChatClientCatalog = new APIClient(spec7);

//CALLING
const spec6 = createClientSpec({
  withCredentials: false,
  baseURL: env.WEB_SERVICE_CALLING_BASE_URI,
  logRequests: env.APP_ENV !== 'production',
});

export const B2ChatClientCalls = new APIClient(spec6);

//MERCADO PAGO
const spec8 = {
  ...createClientSpec({
    withCredentials: false,
    baseURL: env.WEB_SERVICE_MERCADOPAGO_BASE_URI,
    logRequests: env.APP_ENV !== 'production',
  }),
  headers: {},
};

export const B2ChatClientMercadoPago = new APIClient(spec8);

export default B2ChatClient;

export type ClientResource = (
  options?: ClientResourceOptions
) => ClientResponse;

export type BaseQueryArgs = {
  resource: ClientResource;
  options: ClientResourceOptions & {
    /**
     * A function to transform an error result into a desired format.
     *
     * @param error - The original error result or serialized error object.
     * @returns The transformed error query or data query return value.
     */
    transformError?: (
      error: QueryErrorResult | SerializedError
    ) => QueryReturnValue;
  };
};

type QueryErrorResult = {
  status: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: Record<string | number, any>;
};

const QueryErrorResultSym = Symbol.for('QueryErrorResult');

export const b2chatBaseQuery = retry(
  (async (args: BaseQueryArgs, api) => {
    const { resource } = args;
    const { transformError, ...options } = args.options;

    try {
      options.signal = api.signal;
      const response = await resource(options);

      if (response.error) {
        const error = {
          brand: QueryErrorResultSym,
          status: response.error.status,
          data: response.error,
        } as QueryErrorResult;

        // Global session-expired handler: redirect to /login on 401 when the
        // user is on an authenticated route. Public routes (activation links,
        // password reset, etc.) are excluded via isPublicRoute().
        if (response.error.status === 401 && !isPublicRoute()) {
          window.location.href = '/login';
        }

        if (transformError) return transformError(error);
        return { error };
      }
      return { data: response.data };
    } catch (unknownError) {
      const error = miniSerializeError(unknownError);

      if (transformError) return transformError(error);
      return { error };
    }
  }) as BaseQueryFn<BaseQueryArgs>,
  { maxRetries: 0 }
);

export function apiResource(
  resource: ClientResource,
  options: BaseQueryArgs['options']
) {
  return { resource, options };
}

export function isQueryErrorResult(error: unknown): error is QueryErrorResult {
  return (error as { brand: symbol })?.brand === QueryErrorResultSym;
}

export { APIClient, APIClientSpec };
